import { Dialog, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { DialogEnum, useDialog } from "../contexts/DialogContext";
import { RoutePaths } from "../routes";

const MobileNav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const dialog = useDialog();
  const auth = useAuth();
  const [showElectionsSubmenu, setShowElectionsSubmenu] = useState(false);

  return (
    <div className="lg:hidden">
      {/* Mobile menu */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 flex lg:hidden"
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative max-w-xs w-full bg-white shadow-xl pb-12 flex flex-col overflow-y-auto text-primary-blue text-3xl tracking-[0.07rem]">
              <div className="flex px-4 pt-5 pb-2 bg-blue-50">
                <button
                  type="button"
                  className="inline-flex items-center justify-center p-2 pb-4 -m-2 rounded-md outline-none text-primary-blue"
                  onClick={() => setOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon className="w-10 h-10" aria-hidden="true" />
                </button>
              </div>

              {/* Menu */}
              <div className="px-6 py-20 space-y-6 border-t border-gray-200">
                {auth.isLoggedIn ? (
                  auth.isAdmin ? (
                    <>
                      <div className="flow-root">
                        <div
                          onClick={() =>
                            setShowElectionsSubmenu((prev) => !prev)
                          }
                          className={`cursor-pointer`}
                        >
                          Elections
                        </div>
                        {showElectionsSubmenu && (
                          <div className="pt-1 pl-4">
                            <ul>
                              <li className="pt-2 text-[20px]">
                                <Link
                                  to={`/elections${RoutePaths.GENERAL_CHARTS}`}
                                  className={`${location.pathname.startsWith(
                                    `/elections${RoutePaths.GENERAL_CHARTS}`
                                  )
                                      ? "border-b-2 border-red-500"
                                      : ""
                                    }`}
                                >
                                  General
                                </Link>
                              </li>
                              <li className="pt-2 text-[20px]">
                                <Link
                                  to={`/elections${RoutePaths.PARTISAN_CHARTS}`}
                                  className={`${location.pathname.startsWith(
                                    `/elections${RoutePaths.PARTISAN_CHARTS}`
                                  )
                                      ? "border-b-2 border-red-500"
                                      : ""
                                    }`}
                                >
                                  Partisan
                                </Link>
                              </li>
                              <li className="pt-2 text-[20px]">
                                <Link
                                  to={`/elections${RoutePaths.NON_PARTISAN_CHARTS}`}
                                  className={`${location.pathname.startsWith(
                                    `/elections${RoutePaths.NON_PARTISAN_CHARTS}`
                                  )
                                      ? "border-b-2 border-red-500"
                                      : ""
                                    }`}
                                >
                                  Non-Partisan
                                </Link>
                              </li>
                              <li className="pt-2 text-[20px]">
                                <Link
                                  to={`/elections${RoutePaths.PRESIDENTIAL_CHARTS}`}
                                  className={`${location.pathname.startsWith(
                                    `/elections${RoutePaths.PRESIDENTIAL_CHARTS}`
                                  )
                                      ? "border-b-2 border-red-500"
                                      : ""
                                    }`}
                                >
                                  Presidential
                                </Link>
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="flow-root">
                        <Link
                          to={RoutePaths.RULE_SETS}
                          className={`${location.pathname.startsWith(RoutePaths.RULE_SETS)
                              ? "border-b-2 border-red-500"
                              : ""
                            }`}
                        >
                          Rule Sets
                        </Link>
                      </div>
                      <div className="flow-root">
                        <Link
                          to="/rules"
                          className={`${location.pathname.startsWith("/rules") &&
                              !location.pathname.includes("/rulesets")
                              ? "border-b-2 border-red-500"
                              : ""
                            }`}
                        >
                          Rules
                        </Link>
                      </div>
                      <div className="flow-root">
                        <Link
                          to="/clerks"
                          className={`${location.pathname.startsWith("/clerk")
                              ? "border-b-2 border-red-500"
                              : ""
                            }`}
                        >
                          Clerk
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flow-root">
                        <Link
                          to={RoutePaths.NON_PARTISAN_CHARTS}
                          className={`${location.pathname.startsWith(
                            RoutePaths.NON_PARTISAN_CHARTS
                          )
                              ? "border-b border-red-500"
                              : ""
                            }`}
                        >
                          Non-Partisan / Presidential Primary
                        </Link>
                      </div>
                      <div className="flow-root">
                        <Link
                          to={RoutePaths.GENERAL_CHARTS}
                          className={`${location.pathname.startsWith(
                            RoutePaths.GENERAL_CHARTS
                          )
                              ? "border-b border-red-500"
                              : ""
                            }`}
                        >
                          General
                        </Link>
                      </div>
                      <div className="flow-root">
                        <Link
                          to={RoutePaths.PARTISAN_CHARTS}
                          className={`${location.pathname.startsWith(
                            RoutePaths.PARTISAN_CHARTS
                          )
                              ? "border-b border-red-500"
                              : ""
                            }`}
                        >
                          Partisan Primary
                        </Link>
                      </div>
                      {/*<div className="flow-root">
                        <Link
                          to={RoutePaths.PRESIDENTIAL_CHARTS}
                          className={`${
                            location.pathname.startsWith(
                              RoutePaths.PRESIDENTIAL_CHARTS
                            )
                              ? "border-b border-red-500"
                              : ""
                          }`}
                        >
                          Presidential Primary
                        </Link>
                        </div>*/}
                    </>
                  )
                ) : (
                  <>
                    <button
                      className="px-5 py-3 mr-5 tracking-wider text-blue-800 uppercase transition hover:text-blue-900"
                      onClick={() => dialog.push(DialogEnum.SIGN_IN)}
                    >
                      Log In
                    </button>
                    <a
                      className="px-5 py-3 font-semibold tracking-wider text-white uppercase transition bg-blue-800 rounded-md hover:bg-blue-900"
                      href={RoutePaths.CREATE_ACCOUNT}
                    >
                      Sign Up
                    </a>
                  </>
                )}
              </div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>
      <div className="flex items-center justify-start p-6 lg:hidden">
        <div className="flex justify-between w-16">
          <button onClick={() => setOpen(true)}>
            <Bars3Icon className="w-10 h-10 text-primary-blue" />
          </button>
        </div>
        <div className="pl-4">
          <Link to={RoutePaths.LANDING}>
            <img src="/logo.png" alt="ballot chart logo" />
          </Link>
        </div>
      </div>
      <hr />
    </div>
  );
};

export default MobileNav;
